import api from "@/libs/axios";
import { QueryConfig } from "@/libs/query";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiEnum } from "@/pages/taskpane/shared/types/enums/apiEnum";
import { QUERY_KEY } from "@/pages/taskpane/shared/constants/queryKey";
import { DeskLocationEntity, ResourceEntity } from "../../resources/services/types/resourceEntity";
import { CategoryOperator, ComparisonOperator, FilterTypeEnum } from "@/pages/taskpane/shared/types/enums/globalEnum";

export type FilterResourcesPayloadType = {
    pageSize?: number;
    nextIndex?: number;
    requestId: string;
    organizer?: string;
    locationIds?: number[];
    resourceType?: string;
    isNoEndDate?: boolean | null;
    isRecurring?: boolean;
    timeZoneStandardName?: string;
    attendees?: string[];
    occurrences?: {
        utcStartDate: string;
        utcEndDate: string;
    }[];
    name?: string;
    isOnlyAvailable: boolean;
    uid: string;
    globalId: string;
    sortBy: {
        field: string;
        operator: string;
    };
    filterCategory?: {
        categoryIds: number[];
        operator: string;
    } | null;
    filterCapacity?: {
        capacity: number;
        operator: string;
    } | null;
    filterType?: FilterTypeEnum; // 0 -> all, 1-> only favorite, 2-> only mostused
};

const filterResources = async (payload: FilterResourcesPayloadType) => {
    const { data } = await api.post(apiEnum.filterResources, payload);
    return {
        isLastPage: data?.data?.isLastPage || false,
        nextIndex: data?.data?.nextIndex || 0,
        deskLocations: (data?.data?.deskLocations || []).map((item: any) => new DeskLocationEntity(item)),
        resources: (data?.data?.resources || []).map((item: any) => new ResourceEntity(item)),
    };
};

const getFilterTypeQueryKey = (filterType?: FilterTypeEnum): string => {
    if (filterType === FilterTypeEnum.Favorite) {
        return QUERY_KEY.FILTER_RESOURCES_FAVORITE;
    }
    if (filterType === FilterTypeEnum.MostUsed) {
        return QUERY_KEY.FILTER_RESOURCES_MOST_USED;
    }
    return QUERY_KEY.FILTER_RESOURCES;
};

type FilterResourcesType = {
    queryConfig?: QueryConfig<any>;
    payload: FilterResourcesPayloadType;
    enabled?: boolean;
};

export const useFilterResources = ({ queryConfig, payload, enabled = true }: FilterResourcesType) => {
    // Chỉ serialize payload khi enabled=true để tránh vòng lặp
    const serializedPayload = useMemo(() => {
        if (!enabled) {
            return null;
        }
        // Tạo bản sao để tránh mutate array gốc
        const sortedLocationIds = payload.locationIds ? [...payload.locationIds].sort().join(',') : '';
        const sortedAttendees = payload.attendees ? [...payload.attendees].sort().join(',') : '';
        const occurrencesHash = payload.occurrences 
            ? JSON.stringify(payload.occurrences.map(o => ({ start: o.utcStartDate, end: o.utcEndDate })))
            : '';
        
        return JSON.stringify({
            organizer: payload.organizer || '',
            resourceType: payload.resourceType || '',
            locationIds: sortedLocationIds,
            timeZone: payload.timeZoneStandardName || '',
            occurrencesHash,
            capacity: payload.filterCapacity?.capacity || 0,
            capacityOperator: payload.filterCapacity?.operator || '',
            attendeesHash: sortedAttendees,
            isNoEndDate: payload.isNoEndDate || false,
            isRecurring: payload.isRecurring || false,
            name: payload.name || '',
            isOnlyAvailable: payload.isOnlyAvailable || false,
            uid: payload.uid || '',
            globalId: payload.globalId || '',
            sortBy: payload.sortBy || { field: 'name', operator: 'asc' },
            filterCategory: payload.filterCategory || { categoryIds: [], operator: CategoryOperator.Or },
            filterCapacity: payload.filterCapacity || { capacity: 0, operator: ComparisonOperator.MoreThan },
            requestId: payload.requestId || '',
            pageSize: payload.pageSize || 10,
            filterType: payload.filterType,
        });
    }, [
        enabled,
        payload.organizer,
        payload.resourceType,
        payload.locationIds?.length ? payload.locationIds.slice().sort().join(',') : '',
        payload.timeZoneStandardName,
        payload.occurrences?.length ? payload.occurrences.map(o => `${o.utcStartDate}-${o.utcEndDate}`).join('|') : '',
        payload.filterCapacity?.capacity,
        payload.filterCapacity?.operator,
        payload.attendees?.length ? payload.attendees.slice().sort().join(',') : '',
        payload.isNoEndDate,
        payload.isRecurring,
        payload.name,
        payload.isOnlyAvailable,
        payload.uid,
        payload.globalId,
        payload.sortBy?.field,
        payload.sortBy?.operator,
        payload.filterCategory?.categoryIds?.length ? payload.filterCategory.categoryIds.slice().sort().join(',') : '',
        payload.filterCategory?.operator,
        payload.requestId,
        payload.pageSize,
        payload.filterType,
    ]);

    const queryKey = useMemo(
        () => {
            if (!serializedPayload) {
                return [
                    QUERY_KEY.GET_RESOURCES,
                    getFilterTypeQueryKey(undefined),
                    undefined
                ];
            }
            const parsedPayload = JSON.parse(serializedPayload);
            return [
                QUERY_KEY.GET_RESOURCES,
                getFilterTypeQueryKey(parsedPayload.filterType),
                parsedPayload
            ];
        },
        [serializedPayload]
    );

    const gatedPayload: FilterResourcesPayloadType | undefined = enabled ? payload : undefined;

    const query = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam = 0 }) => {
            if (!gatedPayload) {
                throw new Error('Cannot fetch resources when payload is undefined');
            }
            return filterResources({ ...gatedPayload, nextIndex: pageParam });
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage?.isLastPage) {
                return undefined;
            }
            return allPages[allPages.length - 1]?.nextIndex || 0;
        },
        enabled, // chỉ fetch khi true
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        ...queryConfig,
    });

    return query;
};
